import {
	ChatBubbleLeftRightIcon,
	ExclamationTriangleIcon,
	PaperAirplaneIcon,
	UserGroupIcon,
} from '@heroicons/react/24/outline'
import type { AnalyticsWhatsAppCampaignAnalyticsResponse } from '@jobilla/nexus-sdk'
import { motion } from 'motion/react'

type WhatsAppCampaignAnalyticsProps = {
	data: AnalyticsWhatsAppCampaignAnalyticsResponse | null
	isLoading: boolean
	dateRange: { start_date: string; end_date: string }
	onDateRangeChange: (startDate: string, endDate: string) => void
}

export function WhatsAppCampaignAnalytics(
	{ data, isLoading, dateRange, onDateRangeChange }: WhatsAppCampaignAnalyticsProps,
) {
	const containerClassName =
		'relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm'

	if (isLoading) {
		return (
			<div className={containerClassName}>
				<div className="space-y-4">
					<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500">WhatsApp</h2>
					<div className="grid grid-cols-3 gap-4">
						{[0, 1, 2].map((i) => <MetricSkeleton key={i} delay={i * 0.1} />)}
					</div>
				</div>
			</div>
		)
	}

	if (!data || (data.totalOutbound === 0 && data.totalInbound === 0)) {
		return null
	}

	return (
		<div className={containerClassName}>
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500">WhatsApp</h2>
					<div className="flex items-center gap-2">
						<input
							type="date"
							aria-label="Start date"
							value={dateRange.start_date}
							onChange={(e) => onDateRangeChange(e.target.value, dateRange.end_date)}
							className="text-sm border border-grey-200 rounded-lg px-3 py-1.5"
						/>
						<span className="text-grey-400">-</span>
						<input
							type="date"
							aria-label="End date"
							value={dateRange.end_date}
							onChange={(e) => onDateRangeChange(dateRange.start_date, e.target.value)}
							className="text-sm border border-grey-200 rounded-lg px-3 py-1.5"
						/>
					</div>
				</div>

				<div className="grid grid-cols-3 gap-4">
					<MetricCard
						label="Messages Sent"
						value={data.totalOutbound}
						icon={<PaperAirplaneIcon className="size-5" />}
						color="emerald"
						delay={0}
					/>
					<MetricCard
						label="Delivery Rate"
						value={formatPercent(data.deliveryRate)}
						icon={<ChatBubbleLeftRightIcon className="size-5" />}
						color="sky"
						delay={0.1}
					/>
					<MetricCard
						label="Failed"
						value={data.failedCount}
						icon={<ExclamationTriangleIcon className="size-5" />}
						color={data.failedCount > 0 ? 'orange' : 'slate'}
						delay={0.2}
					/>
				</div>

				<div className="grid grid-cols-3 gap-4">
					<MetricCard
						label="Candidates Contacted"
						value={data.candidatesContacted}
						icon={<UserGroupIcon className="size-5" />}
						color="purple"
						delay={0.1}
					/>
					<MetricCard
						label="Replied"
						value={`${data.candidatesReplied} (${formatPercent(data.replyRate)})`}
						icon={<ChatBubbleLeftRightIcon className="size-5" />}
						color="teal"
						delay={0.2}
					/>
					<MetricCard
						label="Read Rate"
						value={formatPercent(data.readRate)}
						icon={<ChatBubbleLeftRightIcon className="size-5" />}
						color="indigo"
						delay={0.3}
					/>
				</div>

				{data.templateBreakdown.length > 0 && (
					<div>
						<h3 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-3">
							Template Performance
						</h3>
						<div className="border border-grey-200 rounded-lg overflow-hidden">
							<table className="w-full text-sm">
								<thead>
									<tr className="bg-grey-50 text-grey-600">
										<th className="text-left px-4 py-2.5 font-medium">Template</th>
										<th className="text-right px-4 py-2.5 font-medium">Sent</th>
										<th className="text-right px-4 py-2.5 font-medium">Delivered</th>
										<th className="text-right px-4 py-2.5 font-medium">Read</th>
										<th className="text-right px-4 py-2.5 font-medium">Failed</th>
									</tr>
								</thead>
								<tbody>
									{data.templateBreakdown.map((template, index) => (
										<tr
											key={template.templateId ?? `unknown-${index}`}
											className="border-t border-grey-100"
										>
											<td className="px-4 py-2.5 text-grey-900">
												{template.templateName ?? 'Unknown'}
											</td>
											<td className="text-right px-4 py-2.5 text-grey-700">
												{template.sentCount}
											</td>
											<td className="text-right px-4 py-2.5 text-grey-700">
												{template.deliveredCount}
											</td>
											<td className="text-right px-4 py-2.5 text-grey-700">
												{template.readCount}
											</td>
											<td className="text-right px-4 py-2.5 text-grey-700">
												{template.failedCount > 0
													? (
														<span className="text-orange-600">
															{template.failedCount}
														</span>
													)
													: template.failedCount}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

function MetricCard({
	label,
	value,
	icon,
	color,
	delay = 0,
}: {
	label: string
	value: string | number
	icon: React.ReactNode
	color: 'emerald' | 'sky' | 'orange' | 'slate' | 'purple' | 'teal' | 'indigo'
	delay?: number
}) {
	const styles = {
		emerald: {
			icon: 'bg-emerald-500 text-white',
			border: 'border-emerald-200',
			bg: 'bg-emerald-50/50',
			value: 'text-emerald-700',
		},
		sky: { icon: 'bg-sky-500 text-white', border: 'border-sky-200', bg: 'bg-sky-50/50', value: 'text-sky-700' },
		orange: {
			icon: 'bg-orange-500 text-white',
			border: 'border-orange-200',
			bg: 'bg-orange-50/50',
			value: 'text-orange-700',
		},
		slate: {
			icon: 'bg-slate-400 text-white',
			border: 'border-slate-200',
			bg: 'bg-slate-50/50',
			value: 'text-slate-600',
		},
		purple: {
			icon: 'bg-purple-500 text-white',
			border: 'border-purple-200',
			bg: 'bg-purple-50/50',
			value: 'text-purple-700',
		},
		teal: {
			icon: 'bg-teal-500 text-white',
			border: 'border-teal-200',
			bg: 'bg-teal-50/50',
			value: 'text-teal-700',
		},
		indigo: {
			icon: 'bg-indigo-500 text-white',
			border: 'border-indigo-200',
			bg: 'bg-indigo-50/50',
			value: 'text-indigo-700',
		},
	}

	const s = styles[color]

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className={`p-4 rounded-xl border-2 ${s.border} ${s.bg}`}
		>
			<div className="flex items-start justify-between mb-2">
				<div className="text-xs font-semibold text-grey-600 uppercase tracking-wide">{label}</div>
				<div className={`p-1.5 rounded-lg ${s.icon}`}>{icon}</div>
			</div>
			<div className={`text-xl font-bold ${s.value}`}>{value}</div>
		</motion.div>
	)
}

function MetricSkeleton({ delay = 0 }: { delay?: number }) {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, delay }}
			className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50/40"
		>
			<div className="flex items-start justify-between mb-2">
				<div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
				<div className="h-7 w-7 bg-slate-200 rounded-lg animate-pulse" />
			</div>
			<div className="h-6 w-16 bg-slate-200 rounded animate-pulse" />
		</motion.div>
	)
}

function formatPercent(rate: number): string {
	return `${(rate * 100).toFixed(1)}%`
}
